import React, { useState } from 'react';

import { Button, Col, Row, Tab, Table, Tabs } from 'react-bootstrap';
import { css, StyleSheet } from 'aphrodite';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import {
  backgroundDarkGrey,
  backgroundGrey5,
  blackberry,
  coolGrey,
  greyStroke,
  purple1,
  squidInk,
  white,
} from 'lib/css/colors';
import Container from 'components/shared/Container';
import { JOBS } from 'lib/networking/endpoints';
import JobTableRow from 'components/jobs/JobTableRow';
import LoadingSpinner from 'components/shared/LoadingSpinner';
import NewJobModal from 'components/jobs/NewJobModal';
import useGet from 'lib/hooks/useGet';
import UserPermission from 'lib/enums/UserPermission';
import useUserPermissions from 'lib/hooks/useUserPermissions';

const JOB_TABS = {
  EXPORT: 'Exports',
  IMPORT: 'Imports',
  PROCESS: 'Processors',
};

function JobsView() {
  const [activeTabKey, setActiveTabKey] = useState(JOB_TABS.EXPORT);
  const [showNewModal, setShowNewModal] = useState(false);

  const hasUserPermissions = useUserPermissions();
  const canManage = hasUserPermissions([UserPermission.MANAGE_JOBS]);

  const {
    data: { data: exportJobs = [] } = {},
    loading: loadingExport,
    error: errorExport,
    refetch: refetchExport,
  } = useGet(JOBS, { category: 'EXPORT' });

  const { data: { data: importJobs = [] } = {}, refetch: refetchImport } = useGet(JOBS, {
    category: 'IMPORT',
  });

  const { data: { data: processJobs = [] } = {}, refetch: refetchProcess } = useGet(JOBS, {
    category: 'PROCESS',
  });

  const refetchAll = () => {
    refetchExport();
    refetchImport();
    refetchProcess();
  };

  if (loadingExport) {
    return <LoadingSpinner />;
  }

  if (errorExport) {
    return null;
  }

  const tableHeaders = (
    <thead>
      <tr>
        <th className={css(styles.th)}>Name</th>
        <th className={css(styles.th)}>Target</th>
        <th className={css(styles.th)}>Schedule</th>
        <th className={css(styles.th)}>Status</th>
        <th className={css(styles.th)}>Actions</th>
      </tr>
    </thead>
  );

  const renderJobRows = (jobs, refetch) =>
    jobs.map(job => <JobTableRow key={job.id} job={job} refetch={refetch} />);

  return (
    <div className={css(styles.pageContainer)}>
      <div className={css(styles.headerSection)}>
        <div className={css(styles.headerContent)}>
          <h1 className={css(styles.pageTitle)}>Jobs</h1>
          {canManage && (
            <Button className={css(styles.newJobButton)} onClick={() => setShowNewModal(true)}>
              <FontAwesomeIcon icon={faPlus} className="mx-1" />
              New Job
            </Button>
          )}
        </div>
      </div>

      <Container className={css(styles.mainContent)}>
        <Tabs activeKey={activeTabKey} onSelect={key => setActiveTabKey(key)}>
          <Tab eventKey={JOB_TABS.EXPORT} title={JOB_TABS.EXPORT}>
            <div className={css(styles.tableContainer)}>
              <Table hover className={css(styles.mainTable)}>
                {tableHeaders}
                <tbody>{renderJobRows(exportJobs, refetchExport)}</tbody>
              </Table>
            </div>
          </Tab>
          <Tab eventKey={JOB_TABS.IMPORT} title={JOB_TABS.IMPORT}>
            <div className={css(styles.tableContainer)}>
              <Table hover className={css(styles.mainTable)}>
                {tableHeaders}
                <tbody>{renderJobRows(importJobs, refetchImport)}</tbody>
              </Table>
            </div>
          </Tab>
          <Tab eventKey={JOB_TABS.PROCESS} title={JOB_TABS.PROCESS}>
            <div className={css(styles.tableContainer)}>
              <Table hover className={css(styles.mainTable)}>
                {tableHeaders}
                <tbody>{renderJobRows(processJobs, refetchProcess)}</tbody>
              </Table>
            </div>
          </Tab>
        </Tabs>
        {showNewModal && <NewJobModal onHide={() => setShowNewModal(false)} refetch={refetchAll} />}
      </Container>
    </div>
  );
}

const styles = StyleSheet.create({
  pageContainer: {
    minHeight: '100vh',
    backgroundColor: backgroundGrey5,
  },
  headerSection: {
    background: `linear-gradient(135deg, ${blackberry} 0%, ${purple1} 100%)`,
    padding: '24px 40px',
    '@media (max-width: 576px)': {
      padding: '16px 20px',
    },
  },
  headerContent: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    maxWidth: 1600,
    margin: '0 auto',
  },
  pageTitle: {
    fontSize: 28,
    fontWeight: 600,
    color: white,
    margin: 0,
    '@media (max-width: 576px)': {
      fontSize: 22,
    },
  },
  newJobButton: {
    backgroundColor: white,
    color: blackberry,
    border: 'none',
    borderRadius: 8,
    padding: '10px 16px',
    fontWeight: 600,
    fontSize: 14,
    ':hover': {
      backgroundColor: backgroundGrey5,
      color: blackberry,
    },
  },
  mainContent: {
    padding: '24px 40px',
    '@media (max-width: 576px)': {
      padding: '16px 16px',
    },
  },
  tableContainer: {
    backgroundColor: white,
    borderRadius: 12,
    border: `1px solid ${greyStroke}`,
    overflowX: 'auto',
    marginTop: 16,
  },
  mainTable: {
    backgroundColor: white,
    marginBottom: 0,
  },
  th: {
    color: coolGrey,
    fontWeight: 600,
    fontSize: 13,
    whiteSpace: 'nowrap',
    backgroundColor: backgroundGrey5,
  },
});

export default JobsView;
